import { productAvailabilityLabels, productAvailabilityTypes } from '@/app/library/constants';

export const LIKESHOP_ROUTE = Object.freeze({
  INSTAGRAM: 'instagram.likeshop',
  TIKTOK: 'tiktok.likeshop',
});

export const LIKESHOP_GALLERY_ID = Object.freeze({
  INSTAGRAM: 'likeshop',
  TIKTOK: 'tiktok_likeshop',
});

export const LIKESHOP_PAGE_TITLE = Object.freeze({
  INSTAGRAM: 'Instagram LikeShop',
  TIKTOK: 'TikTok LikeShop',
});

export const LIKESHOP_DRAWERS = Object.freeze({
  SETTINGS: 'likeShopSettings',
});

export const ECOMMERCE_DRAWERS = Object.freeze({
  MEDIA_LINKS: 'mediaLinks',
  PRODUCT: 'product',
  PRODUCT_DETAILS: 'productDetails',
  ADD_PRODUCT: 'addProduct',
  PRODUCT_PAGE_GALLERY_SETTINGS: 'productPageGallerySettings',
  UTM_SETTINGS: 'utmSettings',
});

export const LIKESHOP_MEDIA_TYPES = Object.freeze({
  VIDEO: 'VIDEO',
  IMAGE: 'IMAGE',
});

export const LIKESHOP_COPY_TOOLTIP = Object.freeze({
  INSTAGRAM:
    'This is your custom LikeShop website address. Add it to your Instagram bio to make it visible to your followers.',
  TIKTOK:
    'This is your custom LikeShop website address. Add it to your TikTok bio to make it visible to your followers.',
});

export const LIKESHOP_COPIED_TOAST_MESSAGE = Object.freeze({
  INSTAGRAM: 'Add this link to your Instagram bio to share it with your followers',
  TIKTOK: 'Add this link to your TikTok bio to share it with your followers',
});

export const LIKESHOP_FIRST_TIME_BANNER = Object.freeze({
  TITLE: 'Set Up Your Link in Bio',
  IG_MESSAGE:
    'Convert your Instagram followers into shoppers with LikeShop. Get started by adding product links to at least your last 12 posts.',
  TIKTOK_MESSAGE:
    'Convert your TikTok followers into shoppers with LikeShop. Get started by adding product links to at least your last 12 posts.',
});

export const LIKESHOP_ALL_MEDIA_SORT_OPTIONS = Object.freeze([
  {
    value: 'ORDER',
    label: 'Customized Order',
  },
  {
    value: 'POSTED',
    label: 'Recently Published',
  },
  {
    value: 'CLICKS',
    label: 'Clicks',
  },
]);

export const LIKESHOP_MEDIA_TYPE_OPTIONS = Object.freeze([
  {
    value: 'ALL_MEDIA',
    label: 'All Media',
  },
  {
    value: 'MEDIA_WITH_LINKS',
    label: 'Media with Links',
  },
]);

export const likeshopAllMediaDefaultOptions = Object.freeze({
  sort: {
    sortOrder: 'ASC',
    selected: LIKESHOP_ALL_MEDIA_SORT_OPTIONS[0],
  },
  dateRangeFilter: {
    preset: 'ALL_TIME',
    presetObject: { value: 'ALL_TIME', label: 'All Time' },
  },
  mediaType: 'ALL_MEDIA',
});

export const integrations = Object.freeze({
  introCopy:
    'Turn followers into newsletter subscribers or email leads. Download submissions as a CSV or send them directly to your Mailchimp or Klaviyo lists.',
  mailchimpHelpLink:
    'https://help.dashsocial.com/hc/en-us/articles/14530160226189-Why-Connect-Your-Mailchimp-Account-with-Dash-Hudson-LikeShop',
  klaviyoHelpLink:
    'https://help.dashsocial.com/hc/en-us/articles/14528999094925-Why-Connect-Your-Klaviyo-Account-with-Dash-Hudson-LikeShop',
});

export const PRIVACY_POLICY_LINK_TOOLTIP =
  'Link provided to your audience outlining your privacy policy. Required as per the Interactive Features section of our Terms of Service and applicable legislation.';

export const MAIN_CONTENT_TOOLTIP = 'Only media with links will appear on your live LikeShop';

export const PRODUCT_SORT_OPTIONS = [
  { label: 'Recently Added', value: 'DATE' },
  { label: 'Total Clicks', value: 'CLICKS' },
];

export const LIKESHOP_SCOPED_FILTERS = Object.freeze({
  PRODUCTS: {
    scope: 'likeshopProducts',
    options: {
      sort: PRODUCT_SORT_OPTIONS,
      importSource: [
        {
          formattedLabel: 'Facebook Product Catalog',
          label: 'Facebook Product Catalog',
          value: 'facebook-product-catalog',
        },
        {
          formattedLabel: 'Single Product Link',
          label: 'Single Product Link',
          value: 'single-product-link',
        },
        {
          formattedLabel: 'Other Product Feeds',
          label: 'Custom Product Feeds',
          value: 'other-product-feed',
        },
      ],
      availability: [
        {
          label: productAvailabilityLabels.IN_STOCK,
          formattedLabel: productAvailabilityLabels.IN_STOCK,
          value: productAvailabilityTypes.IN_STOCK,
        },
        {
          label: productAvailabilityLabels.OUT_OF_STOCK,
          formattedLabel: productAvailabilityLabels.OUT_OF_STOCK,
          value: productAvailabilityTypes.OUT_OF_STOCK,
        },
        {
          label: productAvailabilityLabels.PREORDER,
          formattedLabel: productAvailabilityLabels.PREORDER,
          value: productAvailabilityTypes.PREORDER,
        },
        {
          label: productAvailabilityLabels.AVAILABLE_FOR_ORDER,
          formattedLabel: productAvailabilityLabels.AVAILABLE_FOR_ORDER,
          value: productAvailabilityTypes.AVAILABLE_FOR_ORDER,
        },
        {
          label: productAvailabilityLabels.DISCONTINUED,
          formattedLabel: productAvailabilityLabels.DISCONTINUED,
          value: productAvailabilityTypes.DISCONTINUED,
        },
      ],
    },
    default: {
      sort: {
        sortOrder: 'DESC',
        selected: PRODUCT_SORT_OPTIONS[0],
      },
      importSource: [],
      availability: [],
      withMediaOnly: false,
      reportingPeriod: {
        preset: 'ALL_TIME',
        presetObject: { value: 'ALL_TIME', label: 'All Time' },
      },
    },
  },
});

export const ADD_LINKS_FORM_FEEDBACK_MESSAGES = Object.freeze({
  INVALID: 'Please enter a valid link starting with https://',
  NEW_PRODUCT:
    'This link is not in your product catalog. Saving it will create a new product in Dash Social.',
});

export const productCardAvailabilityChipMap = Object.freeze({
  [productAvailabilityTypes.IN_STOCK]: {
    label: productAvailabilityLabels.IN_STOCK,
    severity: 'success',
  },
  [productAvailabilityTypes.OUT_OF_STOCK]: {
    label: productAvailabilityLabels.OUT_OF_STOCK,
    severity: 'danger',
  },
  [productAvailabilityTypes.AVAILABLE_FOR_ORDER]: {
    label: productAvailabilityLabels.AVAILABLE_FOR_ORDER,
    style: 'success',
  },
  [productAvailabilityTypes.PREORDER]: {
    label: productAvailabilityLabels.PREORDER,
    style: 'success',
  },
  [productAvailabilityTypes.DISCONTINUED]: {
    label: productAvailabilityLabels.DISCONTINUED,
    style: 'warning',
  },
});

export const PRODUCT_SELECT_PLACEHOLDER = 'product-select-placeholder';

export const DEFAULT_COPY = Object.freeze({
  DESKTOP_CTA: 'Click on media to learn more',
  EMAIL_CAPTURE_DESCRIPTION:
    'Subscribe to our newsletter and be the first to hear about our upcoming launches.',
  EMAIL_CAPTURE_BUTTON: 'Subscribe',
});
